import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Car, CircleDot, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { minutes } from '../lib/format';
import { locateQuietly } from '../lib/locate';
import { useGroup } from '../lib/places';
import { load, save } from '../lib/storage';
import { MY_LOCATION } from '../lib/tripEnds';
import type { Waypoint } from '../lib/types';
import PlaceCard from '../components/PlaceCard';
import PlaceMap from '../components/PlaceMap';
import PlaceSearch from '../components/PlaceSearch';
import { GroupChips } from '../components/Filters';
import { Empty, ErrorNote, Spinner } from '../components/ui';

const NEAR_KEY = 'bbh.near.v1';
const RADII = [10, 25, 50, 100];
const NEAR_LIMIT = 12;

// A typed place is remembered on this device; "my location" is looked up fresh each visit.
function loadNear(): { center: Waypoint | null; radius: number } {
  const stored = load<{ center?: Waypoint | null; radius?: number }>(NEAR_KEY, {});
  return {
    center: stored.center && stored.center.label !== MY_LOCATION ? stored.center : null,
    radius: RADII.includes(stored.radius ?? 0) ? stored.radius! : 25,
  };
}

export default function HomePage() {
  const stats = useQuery({ queryKey: ['stats'], queryFn: api.stats });
  const routes = useQuery({ queryKey: ['routes'], queryFn: api.routes });

  return (
    <>
      <section className="mx-auto flex max-w-7xl flex-col gap-5 px-4 pb-10 pt-12 sm:px-6">
        <p className="eyebrow">Bars &amp; bites worth the detour</p>
        <h1 className="text-[clamp(52px,9vw,112px)] font-black leading-[0.86]">
          Find a backroad stop <span className="text-amber">near you.</span>
        </h1>
        <p className="font-display text-3xl font-black uppercase tracking-wide text-cream sm:text-4xl">Skip the exit.</p>
        <p className="max-w-2xl text-lg text-muted">
          The diner with the pie case, the BBQ shack with smoke out back, the tavern with the jukebox. We skip the chains
          and find the local places worth the drive, one nice stop or a whole trip of them.
        </p>

        <NearMe />
      </section>

      <section className="border-t border-line">
        <div className="mx-auto grid max-w-7xl gap-5 px-4 py-12 sm:px-6 md:grid-cols-3">
          <Feature icon={<Car aria-hidden />} title="On your way" action={{ to: '/plan', label: 'Plan a road trip' }}>
            Driving Florida to Michigan with a night in Chattanooga? Enter your start, stops and end, pick how far off the
            highway you&apos;ll go, and get the bars and bites along the way, in order.
          </Feature>
          <Feature icon={<CircleDot aria-hidden />} title="Around town" action={{ to: '/plan?mode=loop', label: 'Look around a town' }}>
            Staying put? Draw a circle around where you sleep and we&apos;ll find the best spots, or suggest a loop that
            starts and ends there.
          </Feature>
          <Feature icon={<ShieldCheck aria-hidden />} title="Bar hop? Driver first" dd action={{ to: '/my-trip', label: 'Set up a bar hop' }}>
            A bar hop isn&apos;t ready until someone pledges to drive sober. Share it with the group by link or email, with
            the driver named right at the top.
          </Feature>
        </div>
      </section>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-7xl flex-wrap gap-3 px-4 py-8 sm:px-6">
          <Sign value={stats.data ? stats.data.totalPlaces.toLocaleString() : '–'} label="Places mapped" />
          <Sign value={stats.data ? stats.data.barCount.toLocaleString() : '–'} label="Bars" />
          <Sign value={stats.data ? stats.data.biteCount.toLocaleString() : '–'} label="Bites" />
          <Sign value={stats.data ? String(stats.data.states.length) : '–'} label="States" />
          <Sign value={stats.data ? String(stats.data.routeCount) : '–'} label="Hand-built routes" />
          <div className="flex min-w-36 flex-col rounded-lg border-2 border-dd px-4 py-2 text-dd">
            <b className="font-display text-3xl leading-none">1</b>
            <small className="text-xs uppercase tracking-[0.1em]">Sober driver, every bar hop</small>
          </div>
        </div>
      </section>

      <section className="border-t border-line">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <h2 className="section-title">Hand-built routes</h2>
            <Link to="/routes">All routes →</Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {routes.data?.map((r) => (
              <Link key={r.slug} to={`/routes/${r.slug}`} className="card flex flex-col gap-2 p-5 text-cream no-underline hover:border-amber hover:text-cream">
                <span className="text-[13px] font-bold uppercase tracking-[0.1em] text-amber">{r.badge ?? r.region}</span>
                <span className="font-display text-3xl font-black uppercase leading-none">{r.name}</span>
                <span className="text-sm text-muted">{r.towns.join(' · ')}</span>
                <span className="mt-auto text-sm tabular-nums">
                  {r.stopCount} stops · ~{r.estimatedMiles} mi · ~{minutes(r.estimatedMinutes)} driving
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-4 py-12 sm:px-6">
          <div className="max-w-xl">
            <h2 className="section-title">Own a backroad spot?</h2>
            <p className="mt-3 text-muted">
              Diner, smokehouse, supper club or tavern: claim your listing to keep your hours current, get on a route, and
              (for bars) offer a perk for designated drivers.
            </p>
          </div>
          <Link to="/owners" className="btn btn-primary no-underline">
            Claim your listing
          </Link>
        </div>
      </section>
    </>
  );
}

/** "My wife and I want one nice spot near us": where, bars or bites, how far, then the best nearby. */
function NearMe() {
  const [initial] = useState(loadNear);
  const [center, setCenter] = useState<Waypoint | null>(initial.center);
  const [radius, setRadius] = useState(initial.radius);
  const [group, setGroup] = useGroup();
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => save(NEAR_KEY, { center: center?.label === MY_LOCATION ? null : center, radius }), [center, radius]);

  // Location already allowed (and nothing typed): start from here without a prompt.
  useEffect(() => {
    if (center) return;
    let live = true;
    void locateQuietly().then((at) => live && at && setCenter((c) => c ?? { label: MY_LOCATION, ...at }));
    return () => {
      live = false;
    };
  }, []);

  const near = useQuery({
    queryKey: ['near', center?.lat, center?.lng, radius, group],
    queryFn: () =>
      api.searchPlaces({ nearLat: center!.lat, nearLng: center!.lng, radiusMiles: radius, group, sort: 'score', pageSize: NEAR_LIMIT }),
    enabled: !!center,
    placeholderData: keepPreviousData,
  });
  const items = near.data?.items ?? [];
  const what = group === 'bars' ? 'bars' : group === 'bites' ? 'bites' : 'stops';

  return (
    <div className="mt-2 flex flex-col gap-5">
      <div className="card grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-start">
        <PlaceSearch
          label="Where are you?"
          value={center}
          onChange={setCenter}
          placeholder="Tap the crosshair, or type a town"
          locate
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Looking for</span>
          <GroupChips value={group} onChange={setGroup} label="Looking for" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="nearRadius">Within</label>
          <select id="nearRadius" value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="md:w-36">
            {RADII.map((r) => (
              <option key={r} value={r}>
                {r} miles
              </option>
            ))}
          </select>
        </div>
      </div>

      {!center ? (
        <Empty>Tap the crosshair to use your location, or type a town, and we&apos;ll line up the best backroad stops nearby.</Empty>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_460px]">
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted tabular-nums" aria-live="polite">
              {near.isFetching
                ? 'Looking around…'
                : near.data
                  ? near.data.totalCount
                    ? `The best ${Math.min(items.length, near.data.totalCount)} of ${near.data.totalCount.toLocaleString()} ${what} within ${radius} miles of ${center.label === MY_LOCATION ? 'you' : center.label}.`
                    : ''
                  : ''}
            </p>
            {near.isLoading && <Spinner label="Finding stops near you" />}
            {near.error && <ErrorNote error={near.error} />}
            {near.data && items.length === 0 && (
              <Empty>
                No {what} within {radius} miles. Try a bigger distance{group === 'all' ? '' : ', or pick Both'}.
              </Empty>
            )}
            <div className="grid gap-3 xl:grid-cols-2">
              {items.map((p) => (
                <PlaceCard key={p.id} place={p} selected={p.id === selected} onSelect={() => setSelected(p.id)} compact drive />
              ))}
            </div>
            {near.data && near.data.totalCount > items.length && (
              <Link to="/explore" className="self-start text-sm">
                See them all on the map →
              </Link>
            )}
          </div>
          <PlaceMap
            places={items}
            selectedId={selected}
            onSelect={setSelected}
            circle={{ lat: center.lat, lng: center.lng, radiusMiles: radius }}
            fitPoints={[
              [center.lat + radius / 69, center.lng],
              [center.lat - radius / 69, center.lng],
            ]}
            fitKey={`${center.lat},${center.lng},${radius}`}
            className="h-[420px] lg:sticky lg:top-24 lg:h-[560px]"
            label="Map of the best stops near you"
          />
        </div>
      )}
    </div>
  );
}

function Sign({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex min-w-36 flex-col rounded-lg border-2 border-cream px-4 py-2">
      <b className="font-display text-3xl leading-none tabular-nums">{value}</b>
      <small className="text-xs uppercase tracking-[0.1em] text-muted">{label}</small>
    </div>
  );
}

function Feature({
  icon,
  title,
  children,
  dd,
  action,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  dd?: boolean;
  action: { to: string; label: string };
}) {
  return (
    <div className={`flex flex-col gap-3 rounded-2xl border p-6 ${dd ? 'border-dd-line bg-dd-bg' : 'border-line bg-panel'}`}>
      <span className={dd ? 'text-dd' : 'text-amber'}>{icon}</span>
      <h3 className="text-3xl font-black">{title}</h3>
      <p className="text-[15px] text-[#d6d2c2]">{children}</p>
      <Link to={action.to} className={`btn btn-sm mt-auto self-start no-underline ${dd ? '' : 'btn-primary'}`}>
        {action.label}
      </Link>
    </div>
  );
}
